/**
 * The client-side half of the sign-in step machine.
 *
 * Ported from UniTicket. The shape mirrors the server exactly: the server decides the step
 * and this holds the temp token for it. The client never decides it is allowed to skip a
 * step — it could not, since each temp token is only accepted by the endpoint for its own
 * step, but the state machine here makes the same thing true of the UI.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type {
  AuthUser,
  ForceChangePasswordResponse,
  LoginResponse,
  MeResponse,
  RecoveryVerifyResponse,
  TotpConfirmResponse,
  TotpSetupResponse,
  TotpVerifyResponse,
} from "@cocon-stats/shared";
import { isAdmin as roleIsAdmin, isInternal as roleIsInternal } from "@cocon-stats/shared";
import { toast } from "sonner";

import {
  ApiError,
  apiFetch,
  clearPinnedSessionUser,
  fetchRetrying429,
  getPinnedSessionUser,
  pinSessionUser,
  setAccessToken,
  setSessionConflictHandler,
  setSessionExpiredHandler,
  tempFetch,
} from "@/lib/api-client";

type AuthState =
  | { status: "loading" }
  | { status: "unauthenticated" }
  | { status: "password_expired"; tempToken: string }
  | { status: "totp_setup"; tempToken: string }
  | { status: "totp_verify"; tempToken: string }
  | { status: "authenticated"; user: AuthUser };

interface AuthContextType {
  state: AuthState;
  user: AuthUser | null;
  isAdmin: boolean;
  isInternal: boolean;
  isExternal: boolean;
  login: (email: string, password: string) => Promise<void>;
  forceChangePassword: (newPassword: string) => Promise<void>;
  totpSetup: () => Promise<TotpSetupResponse>;
  totpConfirm: (code: string, secret: string) => Promise<string[]>;
  totpVerify: (code: string, rememberDevice?: boolean) => Promise<void>;
  recoveryVerify: (code: string, rememberDevice?: boolean) => Promise<number>;
  logout: () => Promise<void>;
  updateUser: (user: AuthUser) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  // A mirror of state.status readable from module-level callbacks without waiting for a
  // render. Set eagerly wherever we leave the session on purpose, so a 401 that lands
  // afterwards — an in-flight request, parallel calls — is ignored: no "session expired"
  // toast after a deliberate sign-out, and one toast for N parallel 401s rather than N.
  const statusRef = useRef<AuthState["status"]>("loading");
  useEffect(() => {
    statusRef.current = state.status;
  }, [state.status]);

  const user = state.status === "authenticated" ? state.user : null;

  /**
   * Another tab signed in as someone else, so the shared refresh cookie no longer matches
   * this tab's pin.
   *
   * ⚠ The pin is deliberately NOT cleared. A reload must go on refusing the foreign
   * identity until somebody signs in here explicitly — otherwise the reload simply adopts
   * whoever the cookie now belongs to, which is the exact failure the pin exists to stop.
   */
  const handleSessionConflict = useCallback(() => {
    if (statusRef.current === "unauthenticated") return;
    statusRef.current = "unauthenticated";
    setAccessToken(null);
    setState({ status: "unauthenticated" });
    toast.error("Your session changed in another tab. Please sign in again.");
  }, []);

  /** The refresh cookie stopped yielding a session. Only meaningful from 'authenticated'. */
  const handleSessionExpired = useCallback(() => {
    if (statusRef.current !== "authenticated") return;
    statusRef.current = "unauthenticated";
    setAccessToken(null);
    setState({ status: "unauthenticated" });
    toast.error("Your session has expired. Please sign in again.");
  }, []);

  useEffect(() => {
    setSessionConflictHandler(handleSessionConflict);
    setSessionExpiredHandler(handleSessionExpired);
    return () => {
      setSessionConflictHandler(null);
      setSessionExpiredHandler(null);
    };
  }, [handleSessionConflict, handleSessionExpired]);

  // On mount: try to restore the session from the refresh cookie.
  useEffect(() => {
    let cancelled = false;

    async function tryRestore() {
      try {
        const res = await fetchRetrying429("/internal/v1/auth/refresh", {
          method: "POST",
          credentials: "include",
        });

        if (!res.ok) {
          // 401 means genuinely signed out. Anything else (429 after retries, 5xx) also
          // lands on the login screen, but a reload will retry rather than insisting.
          if (res.status !== 401) console.warn(`Session restore failed: HTTP ${res.status}`);
          if (!cancelled) setState({ status: "unauthenticated" });
          return;
        }

        const { accessToken } = (await res.json()) as { accessToken: string };
        setAccessToken(accessToken);

        const { user: authUser } = await apiFetch<MeResponse>("/auth/me");
        if (cancelled) return;

        const pinned = getPinnedSessionUser();
        if (pinned && pinned !== authUser.id) {
          setAccessToken(null);
          handleSessionConflict();
          return;
        }

        pinSessionUser(authUser.id);
        setState({ status: "authenticated", user: authUser });
      } catch (err) {
        if (err instanceof ApiError && err.status !== 401) {
          console.warn(`Session restore failed: HTTP ${err.status}`);
        }
        if (!cancelled) setState({ status: "unauthenticated" });
      }
    }

    void tryRestore();
    return () => {
      cancelled = true;
    };
  }, [handleSessionConflict]);

  /** Move to whatever the server says comes next. One place, so no step is ever skipped. */
  const applyStep = useCallback((data: LoginResponse | ForceChangePasswordResponse) => {
    switch (data.step) {
      case "AUTHENTICATED":
        pinSessionUser(data.user.id);
        setAccessToken(data.accessToken);
        setState({ status: "authenticated", user: data.user });
        break;
      case "PASSWORD_EXPIRED":
        setState({ status: "password_expired", tempToken: data.tempToken });
        break;
      case "TOTP_SETUP":
        setState({ status: "totp_setup", tempToken: data.tempToken });
        break;
      case "TOTP_VERIFY":
        setState({ status: "totp_verify", tempToken: data.tempToken });
        break;
    }
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await fetch("/internal/v1/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        credentials: "include",
      });

      if (!res.ok) {
        const data = (await res.json().catch(() => ({ message: res.statusText }))) as {
          message?: string;
        };
        throw new ApiError(res.status, data.message || res.statusText);
      }

      applyStep((await res.json()) as LoginResponse);
    },
    [applyStep],
  );

  const forceChangePassword = useCallback(
    async (newPassword: string) => {
      if (state.status !== "password_expired") throw new Error("Not at the password step");
      const data = await tempFetch<ForceChangePasswordResponse>(
        "/auth/force-change-password",
        state.tempToken,
        { newPassword },
      );
      applyStep(data);
    },
    [state, applyStep],
  );

  const totpSetup = useCallback(async (): Promise<TotpSetupResponse> => {
    if (state.status !== "totp_setup") throw new Error("Not at the enrolment step");
    return tempFetch<TotpSetupResponse>("/auth/totp/setup", state.tempToken);
  }, [state]);

  const totpConfirm = useCallback(
    async (code: string, secret: string): Promise<string[]> => {
      if (state.status !== "totp_setup") throw new Error("Not at the enrolment step");
      const data = await tempFetch<TotpConfirmResponse>("/auth/totp/confirm", state.tempToken, {
        code,
        secret,
      });
      pinSessionUser(data.user.id);
      setAccessToken(data.accessToken);
      setState({ status: "authenticated", user: data.user });
      return data.recoveryCodes;
    },
    [state],
  );

  const totpVerify = useCallback(
    async (code: string, rememberDevice?: boolean) => {
      if (state.status !== "totp_verify") throw new Error("Not at the verification step");
      const data = await tempFetch<TotpVerifyResponse>("/auth/totp/verify", state.tempToken, {
        code,
        rememberDevice,
      });
      pinSessionUser(data.user.id);
      setAccessToken(data.accessToken);
      setState({ status: "authenticated", user: data.user });
    },
    [state],
  );

  const recoveryVerify = useCallback(
    async (code: string, rememberDevice?: boolean): Promise<number> => {
      if (state.status !== "totp_verify") throw new Error("Not at the verification step");
      const data = await tempFetch<RecoveryVerifyResponse>("/auth/recovery/verify", state.tempToken, {
        code,
        rememberDevice,
      });
      pinSessionUser(data.user.id);
      setAccessToken(data.accessToken);
      setState({ status: "authenticated", user: data.user });
      return data.remainingCodes;
    },
    [state],
  );

  const updateUser = useCallback((updated: AuthUser) => {
    setState({ status: "authenticated", user: updated });
  }, []);

  const logout = useCallback(async () => {
    // Leave the session BEFORE the network call: a 401 landing during or after a
    // deliberate sign-out must not raise the "session expired" toast.
    statusRef.current = "unauthenticated";
    try {
      await fetch("/internal/v1/auth/logout", { method: "POST", credentials: "include" });
    } catch {
      // Signing out locally regardless — the server-side stamp is best effort.
    }
    setAccessToken(null);
    clearPinnedSessionUser();
    setState({ status: "unauthenticated" });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        state,
        user,
        // Derived from the shared helpers, so the UI and the API agree on what a role means.
        isAdmin: user ? roleIsAdmin(user.role) : false,
        isInternal: user ? roleIsInternal(user.role) : false,
        isExternal: user ? !roleIsInternal(user.role) : false,
        login,
        forceChangePassword,
        totpSetup,
        totpConfirm,
        totpVerify,
        recoveryVerify,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
